import Avatar from '../Avatar/Avatar';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CLIP_STATUS } from '../../constants/projectCreatorConsts';
import { useUserContext } from '../../context/UserContext';
import { parseString } from '../../utils/stringHelper';
import Icon from '../Icon/Icon';
import Menu from '../Menu/Menu';
import MenuItem from '../Menu/MenuItem';
import styles from './StoryboardSceneMenu.module.scss';
import cx from 'classnames';
import NarrativeSettings from '../NarrativeSettings/NarrativeSettings';
import useOutsideClick from '../../hooks/useOutsideClick';

const PREFERENCES_MENU_WIDTH = 400;

const StoryboardSceneMenu = ({
  menuRef,
  closeMenu,
  customStyles,
  hasScript,
  isProcessing,
  videoAuthor,
  author = null,
  status = 0,
  settings = {},
  onDuplicate,
  onDelete,
  onSendReminder,
  onCopyLink,
  onSettingsChanged,
  onInvite,
  userPermissions,
  topOffset = true,
  rightOffset = true,
  onRequestRevise,
  onUseAvatar,
}) => {
  const settingsRef = useRef();
  const [openedMenuPosition, setOpenedMenuPosition] = useState({});
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { user, isWebInvitationEnabled, isAvatarEnabled } = useUserContext();
  const [showSetting, setShowSettings] = useState({ show: false });

  const isVideoOwner = useMemo(() => {
    const authorName = videoAuthor?.username;
    const userName = user?.username;
    if (authorName && userName) {
      return authorName === userName;
    } else {
      return false;
    }
  }, [videoAuthor, user]);
  const isVideoEditor = useMemo(() => userPermissions.updateStory, [userPermissions]);

  useOutsideClick(menuRef, () => {
    !showSetting?.show && closeMenu();
  });

  useOutsideClick(settingsRef, () => {
    showSetting.show && setShowSettings({ show: false });
  });

  const handleReadClips = useCallback(
    value => {
      const updatedSettings = { read: value };
      if (!value && settings?.update) {
        updatedSettings.update = false;
      }
      onSettingsChanged(updatedSettings);
    },
    [settings]
  );

  const handleEditClips = useCallback(
    value => {
      const updatedSettings = { update: value };
      if (value && !settings?.read) {
        updatedSettings.read = true;
      }
      onSettingsChanged(updatedSettings);
    },
    [settings]
  );
  const handleSettingsClicked = e => {
    const { left } = e.target.getBoundingClientRect();
    let stickToRight = left <= PREFERENCES_MENU_WIDTH;
    setShowSettings(prevState => ({ show: !prevState.show, stickToRight }));
  };

  useEffect(() => {
    if (menuRef.current) {
      const BOTTOM_MENU_OFFSET = 50;
      const LEFT_MENU_OFFSET = 50;
      const customStylesTop = customStyles?.hasOwnProperty('top') ? customStyles?.top : 0;
      const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
      const { offsetHeight, offsetTop, offsetWidth, offsetLeft, offsetParent } = menuRef.current;
      const menuRect = menuRef.current.getBoundingClientRect();
      const positionY = menuRect.y - offsetTop + BOTTOM_MENU_OFFSET + customStylesTop;
      let top = '';
      if (topOffset) {
        if (positionY + offsetHeight > height) {
          // If menu will cross top and bottom boundaries - show it in the middle
          if (menuRect.y - offsetTop - offsetHeight < 0) {
            if (Math.abs(customStylesTop)) {
              let topPosition = customStylesTop - offsetHeight / 2;
              if (topPosition + offsetHeight >= height) {
                topPosition -= topPosition + offsetHeight - height + 30;
              }
              top = topPosition;
            } else {
              top = '-50%';
            }
          } else {
            // If only bottom boundary is crossed - show menu on the top of three dots button
            top = customStylesTop - offsetHeight - 32;
          }
        } else if (positionY < 0) {
          top = customStylesTop + positionY;
        } else {
          top = customStyles?.top;
        }
      } else {
        top = customStyles?.top;
      }
      let left = '';
      if (customStyles?.hasOwnProperty('left')) {
        left = customStyles.left;
      }
      if (rightOffset) {
        const containerRect = offsetParent.offsetParent.getBoundingClientRect();
        const width = containerRect.x + containerRect.width;
        if (customStyles?.hasOwnProperty('left')) {
          if (left + offsetWidth > width) {
            left = width - offsetWidth - LEFT_MENU_OFFSET;
          }
        } else {
          const positionX = menuRect.x - offsetLeft + LEFT_MENU_OFFSET;
          left = positionX + offsetWidth > width ? '0px' : '';
        }
      }

      const styles = { top, left };
      if (customStyles?.hasOwnProperty('position')) {
        styles.position = customStyles.position;
      }

      if (customStyles?.hasOwnProperty('transform')) {
        styles.transform = customStyles.transform;
      }

      setOpenedMenuPosition(styles);
      setLoading(false);
    }
  }, [menuRef]);

  return (
    <Menu ref={menuRef} customStyles={{ ...openedMenuPosition, opacity: loading ? '0' : '1', minWidth: '244px', width: 'fit-content' }}>
      <>
        {author && (
          <div className={styles.section}>
            <div className={styles.userData}>
              <Avatar
                image={author?.avatar}
                title={parseString(author.name)
                  .charAt(0)
                  .toUpperCase()}
                size="medium"
                className={styles.userAvatar}
              />
              <div className={styles.textContainer}>
                <p className={styles.header}>{t('dashboard.video_list.owner')}</p>
                <p className={styles.username}>
                  {author?.username === user.username ? t('share_folder_modal.first_step.you') : parseString(author.name)}
                </p>
              </div>
            </div>
            {author.username !== videoAuthor.username && isVideoOwner && (
              <div>
                <p className={styles.status}>
                  {t('storyboard.editor.clip_status')}:{' '}
                  <span className={cx(styles.statusLabel, { [styles.videoRecorded]: status === CLIP_STATUS.RECORDED.TYPE })}>
                    {t(Object.values(CLIP_STATUS).find(item => item.TYPE === status)?.TERM)}
                  </span>
                </p>
              </div>
            )}
          </div>
        )}
      </>
      {isWebInvitationEnabled && (isVideoOwner || isVideoEditor) && onInvite && (
        <MenuItem
          onClick={() => {
            closeMenu();
            onInvite();
          }}
          className={cx({ [styles.section]: author })}
        >
          <Icon src="invite" /> <span>{t('storyboard.editor.invite')}</span>
        </MenuItem>
      )}
      {/* Only video author allowed to send a reminder */}
      {isWebInvitationEnabled &&
        isVideoOwner &&
        (status === CLIP_STATUS.NOT_RESPONDED.TYPE || status === CLIP_STATUS.PENDING.TYPE) &&
        author?.username !== user.username && (
          <MenuItem
            onClick={() => {
              onSendReminder();
              closeMenu();
            }}
            className={styles.section}
          >
            <Icon src="airplane" /> <span>{t('storyboard.editor.menu.send_reminder')}</span>
          </MenuItem>
        )}
      {isWebInvitationEnabled && isVideoEditor && (
        <MenuItem
          onClick={() => {
            closeMenu();
            onCopyLink();
          }}
        >
          <Icon src="copy_link" /> <span>{t('storyboard.editor.menu.copy_link')}</span>
        </MenuItem>
      )}
      {isVideoOwner && author?.username !== videoAuthor?.username && (
        <MenuItem onClick={handleSettingsClicked} className={styles.section}>
          <Icon src="preference" /> <span>{t('storyboard.editor.menu.privacy_settings')}</span>
        </MenuItem>
      )}
      {showSetting.show && (
        <div ref={settingsRef} className={cx(styles.settingContainer, { [styles.stickToRight]: showSetting.stickToRight })}>
          <NarrativeSettings
            showHeader={false}
            seeStoryLine={settings.read}
            canEditClips={settings.update}
            handleStoryLine={handleReadClips}
            handleEditClips={handleEditClips}
          />
        </div>
      )}

      {isVideoOwner && onRequestRevise && status === CLIP_STATUS.RECORDED.TYPE && author.username !== videoAuthor.username && (
        <MenuItem
          onClick={() => {
            onRequestRevise();
            closeMenu();
          }}
        >
          <Icon src="revise" /> <span>{t('general.video_action_button.request_revise')}</span>
        </MenuItem>
      )}

      {isVideoEditor && onDuplicate && (
        <MenuItem
          onClick={() => {
            closeMenu();
            onDuplicate();
          }}
        >
          <Icon src="duplicate" /> <span>{t('video.settings.duplicate')}</span>
        </MenuItem>
      )}
      {isVideoEditor && onDelete && (
        <MenuItem
          onClick={() => {
            closeMenu();
            onDelete();
          }}
        >
          <Icon src="bin" /> <span>{t('general.video_action_button.delete')}</span>
        </MenuItem>
      )}
      {(isVideoOwner || isVideoEditor) && hasScript && isAvatarEnabled && onUseAvatar && !isProcessing && (
        <MenuItem
          onClick={() => {
            closeMenu();
            onUseAvatar();
          }}
        >
          <Icon src="avatar" /> <span>{t('storyboard.editor.use_avatar')}</span>
        </MenuItem>
      )}
    </Menu>
  );
};

export default StoryboardSceneMenu;
